import { Injectable, NgZone } from '@angular/core';
import * as Hammer from 'hammerjs';

export interface GestureCallbacks {
  onPanStart?: (centerX: number, centerY: number) => void;
  onPanMove?: (deltaX: number, deltaY: number) => void;
  onPanEnd?: () => void;
  onPinchStart?: (scale: number) => void;
  onPinchMove?: (scaleFactor: number, minWidth: number, minHeight: number, maxWidth: number, maxHeight: number) => void;
  onPinchEnd?: () => void;
  onDoubleTap?: () => void;
  onTextDragStart?: () => void;
  onTextDragMove?: (centerX: number, centerY: number, frameRect: DOMRect) => void;
  onTextDragEnd?: () => void;
  onNativePinchStart?: () => void;
  onNativePinchMove?: (scale: number, minWidth: number, minHeight: number, maxWidth: number, maxHeight: number) => void;
  onNativePinchEnd?: () => void;
  onTextGestureStart?: () => void;
  onTextGestureEnd?: () => void;
  onTextPinchMove?: (scaleDelta: number) => void;
  onTextRotateMove?: (rotationDelta: number) => void;
}

@Injectable({
  providedIn: 'root'
})
export class GestureHandlerService {
  private hammerManagers: any[] = [];
  private textHammerManagers: any[] = [];

  constructor(private ngZone: NgZone) {}

  /**
   * Setup Hammer gestures for image manipulation
   */
  setupImageGestures(
    frameElement: HTMLElement,
    callbacks: GestureCallbacks,
    isTextDragging: () => boolean,
    isTyping: () => boolean,
    getFrameBounds: () => { width: number; height: number }
  ): void {
    // Clean up any existing Hammer instances
    this.destroy();

    // Create a new Hammer manager for the frame
    const frameManager = new Hammer.Manager(frameElement);

    // Create recognizers
    const pinch = new Hammer.Pinch();
    const pan = new Hammer.Pan({
      direction: Hammer.DIRECTION_ALL,
      threshold: 0
    });
    const tap = new Hammer.Tap({ taps: 2 });

    // Add recognizers to the manager
    frameManager.add([pinch, pan, tap]);

    // We want to recognize pinch and pan simultaneously
    pinch.recognizeWith(pan);

    // Handle double tap for reset
    if (callbacks.onDoubleTap) {
      frameManager.on('tap', (e) => {
        if (!isTextDragging()) {
          this.ngZone.run(() => {
            callbacks.onDoubleTap!();
          });
        }
      });
    }

    // Setup pan gestures
    this.setupPanGestures(frameManager, callbacks, isTextDragging, isTyping);

    // Setup pinch gestures
    this.setupPinchGestures(frameManager, callbacks, isTextDragging, isTyping, getFrameBounds);

    // Store the manager for cleanup
    this.hammerManagers.push(frameManager);
  }

  /**
   * Setup pan gestures
   */
  private setupPanGestures(
    manager: HammerManager,
    callbacks: GestureCallbacks,
    isTextDragging: () => boolean,
    isTyping: () => boolean
  ): void {
    let lastTouchX = 0;
    let lastTouchY = 0;

    manager.on('panstart', (e) => {
      // Disable pan if in typing mode
      if (!isTextDragging() && !isTyping()) {
        this.ngZone.run(() => {
          lastTouchX = e.center.x;
          lastTouchY = e.center.y;
          if (callbacks.onPanStart) {
            callbacks.onPanStart(e.center.x, e.center.y);
          }
        });
      }
    });

    manager.on('panmove', (e) => {
      if (!isTextDragging()) {
        this.ngZone.run(() => {
          const deltaX = e.center.x - lastTouchX;
          const deltaY = e.center.y - lastTouchY;

          if (callbacks.onPanMove) {
            callbacks.onPanMove(deltaX, deltaY);
          }

          lastTouchX = e.center.x;
          lastTouchY = e.center.y;
        });
      }
    });

    manager.on('panend', () => {
      this.ngZone.run(() => {
        if (callbacks.onPanEnd) {
          callbacks.onPanEnd();
        }
      });
    });
  }

  /**
   * Setup pinch gestures
   */
  private setupPinchGestures(
    manager: HammerManager,
    callbacks: GestureCallbacks,
    isTextDragging: () => boolean,
    isTyping: () => boolean,
    getFrameBounds: () => { width: number; height: number }
  ): void {
    let lastPinchScale = 1;

    manager.on('pinchstart', (e) => {
      const onTextOverlay = !!(e.srcEvent.target as HTMLElement)?.closest?.('.text-overlay');
      if (onTextOverlay || isTextDragging() || isTyping()) return;
      this.ngZone.run(() => {
        lastPinchScale = e.scale;
        if (callbacks.onPinchStart) {
          callbacks.onPinchStart(e.scale);
        }
      });
    });

    manager.on('pinchmove', (e) => {
      const onTextOverlay = !!(e.srcEvent.target as HTMLElement)?.closest?.('.text-overlay');
      if (onTextOverlay || isTextDragging()) return;
      this.ngZone.run(() => {
        const scaleFactor = e.scale / lastPinchScale;
        const frameBounds = getFrameBounds();

        if (callbacks.onPinchMove) {
          callbacks.onPinchMove(
            scaleFactor,
            frameBounds.width,
            frameBounds.height,
            frameBounds.width * 4,
            frameBounds.height * 4
          );
        }

        lastPinchScale = e.scale;
      });
    });

    manager.on('pinchend', (e) => {
      const onTextOverlay = !!(e.srcEvent.target as HTMLElement)?.closest?.('.text-overlay');
      if (onTextOverlay) return;
      this.ngZone.run(() => {
        lastPinchScale = 1;
        if (callbacks.onPinchEnd) {
          callbacks.onPinchEnd();
        }
      });
    });
  }

  /**
   * Setup text overlay dragging, pinch-to-scale, and two-finger rotate gestures
   */
  setupTextGestures(
    textOverlays: NodeListOf<Element>,
    callbacks: GestureCallbacks,
    getFrameElement: () => HTMLElement | null
  ): void {
    setTimeout(() => {
      textOverlays.forEach(overlay => {
        const textManager = new Hammer.Manager(overlay as HTMLElement);

        const pan = new Hammer.Pan({ direction: Hammer.DIRECTION_ALL, threshold: 0 });
        const pinch = new Hammer.Pinch();
        const rotate = new Hammer.Rotate();

        // All three recognizers must run simultaneously
        pinch.recognizeWith(rotate);
        pinch.recognizeWith(pan);
        rotate.recognizeWith(pan);
        rotate.recognizeWith(pinch);

        textManager.add([pan, pinch, rotate]);

        let lastPinchScale = 1;
        let lastRotation = 0;

        // Pan — only for single finger; suppress when a two-finger gesture is active
        textManager.on('panstart', (e) => {
          if (e.pointers.length > 1) return;
          this.ngZone.run(() => {
            if (callbacks.onTextDragStart) {
              callbacks.onTextDragStart();
            }
            e.srcEvent.stopPropagation();
            e.srcEvent.preventDefault();
          });
        });

        textManager.on('panmove', (e) => {
          if (e.pointers.length > 1) return;
          this.ngZone.run(() => {
            const frame = getFrameElement();
            if (!frame) return;

            const frameRect = frame.getBoundingClientRect();
            if (callbacks.onTextDragMove) {
              callbacks.onTextDragMove(e.center.x, e.center.y, frameRect);
            }

            e.srcEvent.preventDefault();
            e.srcEvent.stopPropagation();
          });
        });

        textManager.on('panend', (e) => {
          if (e.pointers.length > 1) return;
          this.ngZone.run(() => {
            if (callbacks.onTextDragEnd) {
              callbacks.onTextDragEnd();
            }
          });
        });

        // Pinch — adjust font size
        textManager.on('pinchstart', (e) => {
          lastPinchScale = e.scale;
          this.ngZone.run(() => {
            if (callbacks.onTextGestureStart) {
              callbacks.onTextGestureStart();
            }
          });
          e.srcEvent.preventDefault();
        });

        textManager.on('pinchmove', (e) => {
          this.ngZone.run(() => {
            const scaleDelta = e.scale / lastPinchScale;
            lastPinchScale = e.scale;
            if (callbacks.onTextPinchMove) {
              callbacks.onTextPinchMove(scaleDelta);
            }
          });
          e.srcEvent.preventDefault();
        });

        textManager.on('pinchend', () => {
          lastPinchScale = 1;
          this.ngZone.run(() => {
            if (callbacks.onTextGestureEnd) {
              callbacks.onTextGestureEnd();
            }
          });
        });

        // Rotate — adjust text rotation
        textManager.on('rotatestart', (e) => {
          lastRotation = e.rotation;
          this.ngZone.run(() => {
            if (callbacks.onTextGestureStart) {
              callbacks.onTextGestureStart();
            }
          });
          e.srcEvent.preventDefault();
        });

        textManager.on('rotatemove', (e) => {
          this.ngZone.run(() => {
            const rotationDelta = e.rotation - lastRotation;
            lastRotation = e.rotation;
            if (callbacks.onTextRotateMove) {
              callbacks.onTextRotateMove(rotationDelta);
            }
          });
          e.srcEvent.preventDefault();
        });

        textManager.on('rotateend', () => {
          lastRotation = 0;
          this.ngZone.run(() => {
            if (callbacks.onTextGestureEnd) {
              callbacks.onTextGestureEnd();
            }
          });
        });

        this.textHammerManagers.push(textManager);
      });
    }, 200);
  }

  /**
   * Destroy only the text overlay gesture managers (called before re-attaching after text is added)
   */
  destroyTextGestures(): void {
    this.textHammerManagers.forEach(manager => {
      if (manager && typeof manager.destroy === 'function') {
        manager.destroy();
      }
    });
    this.textHammerManagers = [];
  }

  /**
   * Setup native pinch zoom for iOS Safari
   */
  setupNativePinchZoom(
    frameElement: HTMLElement,
    callbacks: GestureCallbacks,
    isTextDragging: () => boolean,
    getInitialDimensions: () => { width: number; height: number },
    getFrameBounds: () => { width: number; height: number }
  ): void {
    // Prevent default zoom behavior
    const gestureHandler = (e: any) => {
      e.preventDefault();
    };

    // Track initial scale from gesture start to normalize the scale value
    // iOS Safari's e.scale can start at values other than 1.0
    let initialGestureScale: number | null = null;

    frameElement.addEventListener('gesturestart', (e: any) => {
      gestureHandler(e);
      // Capture the initial scale value to normalize subsequent scale values
      initialGestureScale = e.scale || 1.0;
      if (callbacks.onNativePinchStart) {
        callbacks.onNativePinchStart();
      }
    }, { passive: false });

    frameElement.addEventListener('gesturechange', (e: any) => {
      gestureHandler(e);
      if (!isTextDragging()) {
        this.ngZone.run(() => {
          if (callbacks.onNativePinchMove) {
            const frameBounds = getFrameBounds();
            // Normalize the scale relative to the initial gesture scale
            // This ensures smooth zooming even if iOS Safari starts with scale != 1.0
            const normalizedScale = initialGestureScale !== null 
              ? e.scale / initialGestureScale 
              : e.scale;
            
            callbacks.onNativePinchMove(
              normalizedScale,
              frameBounds.width,
              frameBounds.height,
              frameBounds.width * 4,
              frameBounds.height * 4
            );
          }
        });
      }
    }, { passive: false });

    frameElement.addEventListener('gestureend', (e: any) => {
      gestureHandler(e);
      initialGestureScale = null;
      if (callbacks.onNativePinchEnd) {
        this.ngZone.run(() => callbacks.onNativePinchEnd!());
      }
    }, { passive: false });
  }

  /**
   * Clean up all Hammer managers
   */
  destroy(): void {
    this.hammerManagers.forEach(manager => {
      if (manager && typeof manager.destroy === 'function') {
        manager.destroy();
      }
    });
    this.hammerManagers = [];
    this.destroyTextGestures();
  }
}

